<template>
  <div class="enhanced-message" :class="messageClass">
    <div class="message-content" v-html="processedContent"></div>
    <div class="message-actions" v-if="message.actions?.length">
      <button 
        v-for="action in message.actions" 
        :key="action.id"
        class="action-btn"
        :class="action.style || 'primary'"
        @click="handleAction(action)"
      >
        {{ action.label }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps(['message'])
const emit = defineEmits(['action'])

const messageClass = computed(() => ({
  'user-message': props.message.type === 'user',
  'assistant-message': props.message.type === 'assistant',
  'system-message': props.message.type === 'system'
}))

const processedContent = computed(() => {
  let content = props.message.content
  
  // Convert URLs to clickable links
  const urlRegex = /(https?:\/\/[^\s]+)/g
  content = content.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="message-link">$1</a>')
  
  // Convert email addresses to mailto links
  const emailRegex = /(\S+@\S+\.\S+)/g
  content = content.replace(emailRegex, '<a href="mailto:$1" class="message-link">$1</a>')
  
  // Convert phone numbers to tel links (basic format)
  const phoneRegex = /(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g
  content = content.replace(phoneRegex, '<a href="tel:$1" class="message-link">$1</a>')
  
  return content
})

function handleAction(action) {
  if (action.type === 'link' && action.url) {
    window.open(action.url, '_blank', 'noopener,noreferrer')
  } else {
    emit('action', action)
  }
}
</script>

<style scoped>
.enhanced-message {
  margin-bottom: 15px;
  padding: 15px 20px;
  border-radius: 15px;
  max-width: 85%;
}

.user-message {
  background: var(--primary-text);
  color: white;
  margin-left: auto;
  text-align: right;
}

.assistant-message {
  background: #e9ecef;
  color: var(--text-primary);
  margin-right: auto;
}

.system-message {
  background: var(--background-light);
  color: var(--text-secondary);
  border: 1px solid #dee2e6;
  margin: 0 auto;
  text-align: center;
  font-style: italic;
  max-width: 70%;
}

.message-content {
  line-height: 1.5;
  margin-bottom: 10px;
}

.message-content:last-child {
  margin-bottom: 0;
}

:deep(.message-link) {
  color: inherit;
  text-decoration: underline;
  font-weight: 500;
}

.user-message :deep(.message-link) {
  color: #fff;
}

.assistant-message :deep(.message-link) {
  color: var(--primary-text);
}

.system-message :deep(.message-link) {
  color: var(--text-primary);
}

.message-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.action-btn.primary {
  background: var(--primary-text);
  color: white;
}

.action-btn.primary:hover {
  background: var(--primary-text-hover);
}

.action-btn.secondary {
  background: var(--secondary-color);
  color: white;
}

.action-btn.secondary:hover {
  background: var(--secondary-dark);
}

.action-btn.success {
  background: #10b981;
  color: white;
}

.action-btn.success:hover {
  background: #059669;
}

.action-btn.warning {
  background: #f59e0b;
  color: white;
}

.action-btn.warning:hover {
  background: #d97706;
}

.action-btn.danger {
  background: #dc3545;
  color: white;
}

.action-btn.danger:hover {
  background: #c82333;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .enhanced-message {
    max-width: 90%;
    padding: 12px 15px;
    margin-bottom: 12px;
    border-radius: 12px;
    font-size: 0.9em;
  }
  
  .system-message {
    max-width: 85%;
  }
  
  .action-btn {
    padding: 8px 12px;
    font-size: 0.8rem;
  }
  
  .message-actions {
    gap: 6px;
    margin-top: 8px;
  }
}
</style>